import React, { useState, forwardRef } from 'react';
import { Box, TextField, Button, Typography, Grid, Snackbar, Alert, CircularProgress } from '@mui/material';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { contactFormStyles } from './ContactForm.styles';
import ReusableTextField from '../../../../components/ReusableTextField/ReusableTextField';

const PhoneTextField = forwardRef((props, ref) => {
  const { value, onChange, ...other } = props;
  return (
    <TextField
      {...other}
      inputRef={ref}
      fullWidth
      label="Phone number"
      variant="outlined"
      value={value || ''}
      onChange={(e) => onChange && onChange(e.target.value)}
    />
  );
});

PhoneTextField.displayName = 'PhoneTextField';

const ContactForm = ({ isInView = false, showHeader = false }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: 'AE',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [errors, setErrors] = useState({});

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: false,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['firstName', 'lastName', 'email', 'subject', 'message'];
    
    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].trim() === '') {
        newErrors[field] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitForm = async () => {
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://formsubmit.co/ajax/contact@royal-vista.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          _subject: 'Contact Us Submission',
          message: `Subject: ${formData.subject || 'Not provided'}\n\nMessage:\n${formData.message}`,
          _captcha: false,
          _template: 'box'
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSnackbarMessage('Thank you for your submission! Your message has been successfully sent');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          country: 'AE',
          subject: '',
          message: '',
        });
        setErrors({});
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSnackbarMessage('Error sending your message, please try again later!');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submitForm();
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const styles = contactFormStyles(isInView);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{...styles.formContainer, pt: showHeader ? {xs: "150px", lg: "135px"} : {xs: "50px", lg: "100px"}}}
    >
      <Box mb={4} maxWidth={{xs: "300px", md: "100%"}}>
        <Typography sx={styles.heading}>Find your dream stay</Typography>
        <Typography sx={styles.subtitle}>talk to our experts today</Typography>
      </Box>


      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ReusableTextField
            label="First name"
            value={formData.firstName}
            onChange={handleChange('firstName')}
            error={errors.firstName}
            helperText={errors.firstName ? '*This field is mandatory' : ''}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ReusableTextField
            label="Last name"
            value={formData.lastName}
            onChange={handleChange('lastName')}
            error={errors.lastName}
            helperText={errors.lastName ? '*This field is mandatory' : ''}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ReusableTextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            error={errors.email}
            helperText={errors.email ? '*This field is mandatory' : ''}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={styles.phoneInputContainer}>
            <PhoneInput
              international
              country={formData.country}
              defaultCountry="AE"
              value={formData.phone}
              onChange={(value) => setFormData({ ...formData, phone: value || '' })}
              onCountryChange={(country) => setFormData({ ...formData, country: country || 'AE' })}
              inputComponent={PhoneTextField}
              countryCallingCodeEditable={false}
            />
          </Box>
        </Grid>
      </Grid>

      <ReusableTextField
        label="Subject"
        value={formData.subject}
        onChange={handleChange('subject')}  
        error={errors.subject}
        helperText={errors.subject ? '*This field is mandatory' : ''}
      />

      <ReusableTextField
        label="Message"
        multiline
        rows={3}
        value={formData.message}
        onChange={handleChange('message')}    
        error={errors.message}
        helperText={errors.message ? '*This field is mandatory' : ''}
      />

      <Button
        type="submit"
        variant="contained"
        disableElevation
        sx={styles.sendButton}
        disabled={isSubmitting}
        startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
      >
       <Typography sx={styles.buttonText}>
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </Typography>
      </Button>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactForm;

